// Admin API Types
// Subscription tier enum matching backend constants
export enum SubscriptionTier {
  STARTER = 'starter',
  ARTIST = 'artist',
  STUDIO = 'studio'
}

export interface AdminUser {
  id: string
  email: string
  firstName?: string
  lastName?: string
  displayName?: string
  photoURL?: string
  gdriveEmail?: string
  firebaseUid: string
  premiumUntil?: string
  activePromoCode?: string
  stripeCustomerId?: string
  subscriptionTier?: SubscriptionTier // Raw DB value for editing (starter/artist/studio)
  effectiveTier?: SubscriptionTier    // Computed tier after expiry check (read-only)
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmTerm?: string
  utmContent?: string
  firstSeenAt?: string
  lastUpdatedAt?: string
  landingPage?: string
  createdAt?: string
  hasGoogleDriveToken: boolean
  googleDriveTokenExpiry?: string
  hasGDriveRootFolder: boolean
  gDriveRootLink?: string
  tierUntil?: string
}

export interface AdminUserListResponse {
  users: AdminUser[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface AdminUsersListResponse {
  users: AdminUser[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface AdminUserUpdateRequest {
  email?: string
  firstName?: string
  lastName?: string
  subscriptionTier?: SubscriptionTier
  premiumUntil?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmTerm?: string
  utmContent?: string
  landingPage?: string
}

export interface AdminPromoCode {
  code: string
  userId?: string
  used: boolean
  activationExpiry?: string
  premiumDuration?: number
  activatedAt?: string
  description?: string
  createdBy?: string
  createdAt?: string
  status: 'unused' | 'used' | 'expired' | 'disabled' | 'active'
  daysUntilExpiry?: number
  userEmail?: string
  subscriptionTier?: SubscriptionTier
}

export interface AdminPromoCodeListResponse {
  promoCodes: AdminPromoCode[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface AdminPromoCodeRequest {
  code: string
  premiumDuration: number
  activationExpiry: string
  description?: string
  subscriptionTier?: SubscriptionTier
}

export interface AdminSubscriptionRequest {
  action: 'upgrade' | 'downgrade' | 'remove_premium' | 'grant_premium'
  tier?: SubscriptionTier
  premiumUntil?: string
  reason?: string
  overrideLimits?: boolean
}

export interface UTMSourceStats {
  source: string
  totalUsers: number
  premiumUsers: number
  conversionRate: number
  revenue: number
}

export interface UTMCampaignStats {
  campaign: string
  source: string
  totalUsers: number
  premiumUsers: number
  conversionRate: number
  revenue: number
}

export interface UTMAnalyticsResponse {
  utmSources: UTMSourceStats[]
  utmCampaigns: UTMCampaignStats[]
  utmContents?: UTMContentStats[]
  designVariants?: DesignVariantStats[]
  summary: {
    totalUsers: number
    totalPremiumUsers: number
    overallConversionRate: number
    topSource: string
    topCampaign: string
  }
}

export interface UTMContentStats {
  content: string
  source: string
  campaign: string
  totalUsers: number
  driveConnected: number
  driveConnectRate: number
  premiumUsers: number
  premiumConvertRate: number
}

export interface DesignVariantStats {
  design: string
  totalUsers: number
  driveConnected: number
  driveConnectRate: number
  premiumUsers: number
  premiumConvertRate: number
}

export interface UTMAnalytics {
  source?: string
  campaign?: string
  totalUsers: number
  premiumUsers?: number
  conversions: number
  conversionRate: number
  revenue: number
}

export interface UserAnalytics {
  newUsersThisWeek: number
  upgradesThisWeek: number
  revenueThisWeek: number
  newUsersThisMonth: number
  upgradesThisMonth: number
  revenueThisMonth: number
}

export interface UserGrowthData {
  date: string
  newUsers: number
  premiumUsers: number
  cumulativeUsers: number
}

export interface SubscriptionAnalytics {
  tier: SubscriptionTier
  count: number
  percentage: number
}

export interface UserAnalyticsResponse {
  growthData: UserGrowthData[]
  subscriptionData: SubscriptionAnalytics[]
  summary: {
    totalUsers: number
    totalPremiumUsers: number
    conversionRate: number
    averageGrowthRate: number
  }
}

export interface DashboardStats {
  users: {
    totalUsers: number
    newUsersToday: number
    newUsersWeek: number
    premiumUsers: number
    activeUsers: number
  }
  subscriptions: {
    starterUsers: number
    artistUsers: number
    studioUsers: number
    expiredUsers: number
  }
  promoCodes: {
    totalCodes: number
    usedCodes: number
    unusedCodes: number
    expiredCodes: number
  }
  recentActivity: AdminActivityEntry[]

  // Optional extended fields (some API versions expose these)
  userStats?: {
    totalUsers: number
    newUsersToday?: number
    newUsersWeek?: number
    previousPeriodUsers?: number
    activeUsers: number
    previousPeriodActiveUsers?: number
    premiumUsers: number
    previousPeriodPremiumUsers?: number
  }
  revenueStats?: {
    totalRevenue?: number
    monthlyRevenue: number
    conversionRate?: number
    previousMonthRevenue?: number
  }
  subscriptionStats?: SubscriptionAnalytics[]
  conversionRates?: {
    overall?: number
    bySource?: Record<string, number>
    byCampaign?: Record<string, number>
    freeToArtist: number
    artistToStudio: number
    overallConversion: number
  }
}

export interface AdminActivityEntry {
  timestamp: string
  action: string
  adminUser: string
  targetUser?: string
  details?: Record<string, any>
}

// API Response Types
export interface ApiResponse<T = any> {
  success?: boolean
  errorMessage?: string
  data?: T
}

export interface PaginationParams {
  page?: number
  limit?: number
  search?: string
}

export interface UserFilters extends PaginationParams {
  utm_source?: string
  utm_campaign?: string
  subscription_tier?: SubscriptionTier
  created_after?: string
  created_before?: string
}

export interface PromoCodeFilters extends PaginationParams {
  status?: 'unused' | 'used' | 'expired'
  created_after?: string
  created_before?: string
}

export interface UTMFilters {
  utm_source?: string
  utm_campaign?: string
  date_after?: string
  date_before?: string
}
